import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  Timestamp,
  QueryConstraint
} from 'firebase/firestore';
import { getDb } from './firestoreDb';
import { CRUDOptions, QueryConfig, BaseEntity } from './types';
import { buildQuery, buildQueryConstraints } from './queryBuilder';

/**
 * Base CRUD Manager for Firestore operations
 * 
 * Provides standardised CRUD operations that eliminate boilerplate code
 * across manager classes. Supports both static class methods and object literal patterns.
 * 
 * @example
 * ```typescript
 * // Simple create
 * const id = await BaseCRUDManager.create('Bulletins', { title: 'Test' });
 * 
 * // With custom options
 * const userId = await BaseCRUDManager.create('Users', userData, {
 *   idField: 'userKey',
 *   timestampMethod: 'client',
 *   customId: 'custom-user-id'
 * });
 * 
 * // Query with filters
 * const results = await BaseCRUDManager.query('Bulletins', {
 *   filters: [{ field: 'organisationId', operator: '==', value: orgId }],
 *   orderBy: [{ field: 'createdAt', direction: 'desc' }],
 *   limit: 10
 * });
 * ```
 */
export class BaseCRUDManager {
  /**
   * Default options for CRUD operations
   */
  private static readonly DEFAULT_OPTIONS: Required<CRUDOptions> = {
    idField: 'id',
    timestampMethod: 'server',
    autoTimestamps: true,
    customId: undefined as any
  };

  /**
   * Merges provided options with defaults
   */
  private static mergeOptions(options?: CRUDOptions): Omit<Required<CRUDOptions>, 'customId'> & { customId?: string } {
    return {
      ...this.DEFAULT_OPTIONS,
      ...options
    };
  }

  /**
   * Gets the appropriate timestamp based on configuration
   */
  private static getTimestamp(method: 'server' | 'client'): Timestamp | ReturnType<typeof serverTimestamp> {
    if (method === 'server') {
      return serverTimestamp() as Timestamp;
    }
    return Timestamp.now();
  }

  /**
   * Transforms a Firestore document to include the ID field
   */
  private static transformDocument<T extends BaseEntity>(
    docSnapshot: any, 
    idField: string
  ): T {
    return {
      ...docSnapshot.data(),
      // Document id always wins over any stored id field
      [idField]: docSnapshot.id,
    } as T;
  }

  /**
   * Creates a new document in Firestore
   * 
   * @param collectionName - Name of the Firestore collection
   * @param data - Document data (without id, createdAt, updatedAt)
   * @param options - CRUD configuration options
   * @returns The document ID
   * 
   * @example
   * ```typescript
   * const id = await BaseCRUDManager.create('Bulletins', {
   *   title: 'New Bulletin',
   *   content: 'Content here'
   * });
   * ```
   */
  static async create<T extends BaseEntity>(
    collectionName: string,
    data: Omit<T, 'id' | 'createdAt' | 'updatedAt'>,
    options?: CRUDOptions
  ): Promise<string> {
    try {
      const opts = this.mergeOptions(options);
      const collectionRef = collection(getDb(), collectionName);

      // Prepare document data
      const documentData: any = { ...data };

      // Add timestamps if enabled
      if (opts.autoTimestamps) {
        const timestamp = this.getTimestamp(opts.timestampMethod);
        documentData.createdAt = timestamp;
        documentData.updatedAt = timestamp;
      }

      // Use setDoc if customId is provided, otherwise use addDoc
      if (opts.customId) {
        const docRef = doc(getDb(), collectionName, opts.customId);
        await setDoc(docRef, documentData);
        return opts.customId;
      } else {
        const docRef = await addDoc(collectionRef, documentData);
        return docRef.id;
      }
    } catch (error) {
      console.error(`Error creating document in ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Gets a single document by ID
   * 
   * @param collectionName - Name of the Firestore collection
   * @param id - Document ID
   * @param options - CRUD configuration options
   * @returns The document or null if not found
   * 
   * @example
   * ```typescript
   * const bulletin = await BaseCRUDManager.getById<IBulletin>('Bulletins', 'bulletin-id');
   * ```
   */
  static async getById<T extends BaseEntity>(
    collectionName: string,
    id: string,
    options?: CRUDOptions
  ): Promise<T | null> {
    try {
      const opts = this.mergeOptions(options);
      const docRef = doc(getDb(), collectionName, id);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        return null;
      }

      return this.transformDocument<T>(docSnap, opts.idField);
    } catch (error) {
      console.error(`Error getting document ${id} from ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Gets all documents from a collection
   * 
   * @param collectionName - Name of the Firestore collection
   * @param options - CRUD configuration options
   * @returns Array of all documents
   * 
   * @example
   * ```typescript
   * const allBulletins = await BaseCRUDManager.getAll<IBulletin>('Bulletins');
   * ```
   */
  static async getAll<T extends BaseEntity>(
    collectionName: string,
    options?: CRUDOptions
  ): Promise<T[]> {
    try {
      const opts = this.mergeOptions(options);
      const querySnapshot = await getDocs(collection(getDb(), collectionName));
      const results: T[] = [];

      querySnapshot.forEach((docSnap) => {
        results.push(this.transformDocument<T>(docSnap, opts.idField));
      });

      return results;
    } catch (error) {
      console.error(`Error getting all documents from ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Updates a document
   * 
   * @param collectionName - Name of the Firestore collection
   * @param id - Document ID
   * @param updates - Partial document data to update
   * @param options - CRUD configuration options
   * 
   * @example
   * ```typescript
   * await BaseCRUDManager.update('Bulletins', 'bulletin-id', {
   *   title: 'Updated Title'
   * });
   * ```
   */
  static async update<T extends BaseEntity>(
    collectionName: string,
    id: string,
    updates: Partial<Omit<T, 'id' | 'createdAt'>>,
    options?: CRUDOptions
  ): Promise<void> {
    try {
      const opts = this.mergeOptions(options);
      const docRef = doc(getDb(), collectionName, id);

      const updateData: any = { ...updates };

      // Add updatedAt timestamp if enabled
      if (opts.autoTimestamps) {
        updateData.updatedAt = this.getTimestamp(opts.timestampMethod);
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error(`Error updating document ${id} in ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Deletes a document
   * 
   * @param collectionName - Name of the Firestore collection
   * @param id - Document ID
   * 
   * @example
   * ```typescript
   * await BaseCRUDManager.delete('Bulletins', 'bulletin-id');
   * ```
   */
  static async delete(
    collectionName: string,
    id: string
  ): Promise<void> {
    try {
      const docRef = doc(getDb(), collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document ${id} from ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Queries documents with filters, ordering, and limits
   * 
   * @param collectionName - Name of the Firestore collection
   * @param config - Query configuration (filters, orderBy, limit)
   * @param options - CRUD configuration options
   * @returns Array of matching documents
   * 
   * @example
   * ```typescript
   * const bulletins = await BaseCRUDManager.query<IBulletin>('Bulletins', {
   *   filters: [
   *     { field: 'organisationId', operator: '==', value: orgId },
   *     { field: 'isUrgent', operator: '==', value: true }
   *   ],
   *   orderBy: [
   *     { field: 'createdAt', direction: 'desc' }
   *   ],
   *   limit: 10
   * });
   * ```
   */
  static async query<T extends BaseEntity>(
    collectionName: string,
    config: QueryConfig,
    options?: CRUDOptions
  ): Promise<T[]> {
    try {
      const opts = this.mergeOptions(options);
      const firestoreQuery = buildQuery(collectionName, config);
      const querySnapshot = await getDocs(firestoreQuery);
      const results: T[] = [];

      querySnapshot.forEach((docSnap) => {
        results.push(this.transformDocument<T>(docSnap, opts.idField));
      });

      return results;
    } catch (error) {
      console.error(`Error querying ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Checks if a document exists
   * 
   * @param collectionName - Name of the Firestore collection
   * @param id - Document ID
   * @returns true if document exists, false otherwise
   * 
   * @example
   * ```typescript
   * const exists = await BaseCRUDManager.exists('Bulletins', 'bulletin-id');
   * ```
   */
  static async exists(
    collectionName: string,
    id: string
  ): Promise<boolean> {
    try {
      const docRef = doc(getDb(), collectionName, id);
      const docSnap = await getDoc(docRef);
      return docSnap.exists();
    } catch (error) {
      console.error(`Error checking existence of document ${id} in ${collectionName}:`, error);
      return false;
    }
  }
}
